// React Query keys for data-provider fetches are [resource, method, params].
// Those return resource data that goes stale between sessions, and persisting it
// is what let an empty contacts list survive a deploy. Offline persistence is
// meant for identity/config — so persist everything EXCEPT resource collections
// and records, which are always refetched fresh.
const RESOURCE_QUERY_METHODS = new Set([
  "getList",
  "getInfiniteList",
  "getOne",
  "getMany",
  "getManyReference",
]);
const NON_PERSISTED_QUERY_PREFIXES = new Set([
  "framing-scene-inspector",
  "identity-workbench",
  "spatial-evidence",
  // MB-051 media bin: 300 s signed URLs of a customer's original uploads and
  // their captions. Expired on reload anyway, and personal data.
  "media-bin",
  "echo-feedback",
  // Live WhatsApp state: inbox triage, chat threads and send capability are
  // polled and must never be served stale from a previous session.
  "whatsapp-inbox",
  "contact-conversation",
  "whatsapp-messaging",
  // Stuck-Echo incidents: live monitoring rows with customer names.
  "echo-incidents",
  "echo-incidents-summary",
  // LLM Config: a live readout of the signed runtime config and the ledger.
  // A model shown from a previous session would be wrong without saying so.
  "llm-config",
  // Marketing v2: live attribution numbers, a drill-down with contact ids,
  // and an availability answer that must never outlive a policy rollback.
  "marketing-v2",
  // Marketing v3: the same live numbers, plus the page-version answer.
  "marketing-v3",
  // Gallery (my.echoflicks.com) usage: per-customer visits and the adopter
  // list, plus the raw portal journey on the contact page.
  "gallery-usage",
  "portal-activity",
  // RE funnel: real-estate lead e-mails, replay links and agency films. The
  // mobile cache lives in localStorage; none of it may be written there.
  "re-funnel",
  // Outreach: agent names and e-mails, film and slot state, email activity.
  // Personal data and live state: never written to localStorage.
  "outreach",
  // Customer profile and AI insights: a customer's preferences, recurring
  // people and message quotes (personal data, G34). An entry ending in ":" is
  // a namespace: every key starting with it is covered.
  "contact-profile:",
]);

const NON_PERSISTED_NAMESPACES = [...NON_PERSISTED_QUERY_PREFIXES].filter(
  (prefix) => prefix.endsWith(":"),
);

export function shouldPersistQuery(query: {
  queryKey: unknown;
  state: { status: string };
}): boolean {
  if (query.state.status !== "success") return false;
  const prefix = Array.isArray(query.queryKey) ? query.queryKey[0] : undefined;
  if (
    typeof prefix === "string" &&
    (NON_PERSISTED_QUERY_PREFIXES.has(prefix) ||
      NON_PERSISTED_NAMESPACES.some((namespace) =>
        prefix.startsWith(namespace),
      ))
  ) {
    return false;
  }
  const method = Array.isArray(query.queryKey) ? query.queryKey[1] : undefined;
  return typeof method !== "string" || !RESOURCE_QUERY_METHODS.has(method);
}
